/**
 * A qué organización entra alguien que pertenece a varias, y cuándo hay que preguntárselo.
 *
 * ── LA DEUDA QUE CIERRA ─────────────────────────────────────────────────────
 * `OrganizationContext.jsx` resolvía el empate con `userOrgs[0]`, y ese «primero» no
 * significa nada: es el orden en que Firestore devuelve la consulta de pertenencias
 * (`server/src/utils/miembrosDelUsuario.cjs`), o sea por ruta del documento —
 * `organizations/{orgId}/members/{uid}`— con los del respaldo por correo AÑADIDOS AL
 * FINAL. Un miembro que pierde el campo `uid` salta de posición y el «primero» cambia
 * sin que nadie haya tocado nada.
 *
 * Peor que el orden: con una elección guardada que ya no vale —te sacaron de esa
 * organización— el código caía a `userOrgs[0]` Y SOBRESCRIBÍA el valor guardado sin
 * decir nada. Aparecías dentro de otra organización sin ninguna señal.
 *
 * ── POR QUÉ UNA FUNCIÓN PURA ────────────────────────────────────────────────
 * Porque la decisión ES el producto, y encadenada en tres `else if` dentro de un efecto
 * que habla con Firestore no se puede ejercitar. Aquí se ejecuta, con la lista de
 * entrada que uno quiera. CommonJS igual que `nombreParaSaludo.js`: así la carga la
 * suite de `functions/`, que es la única puerta de build automática del repositorio.
 *
 * ── LO QUE NO DECIDE ────────────────────────────────────────────────────────
 * No sabe de React, ni de Firestore, ni de localStorage. Recibe `savedOrgId` ya
 * resuelto por quien llama (Firestore ?? localStorage) y devuelve qué hacer. Tampoco
 * limpia nada: señala `guardadaInvalida` y quien llama decide cuándo limpiar — que es
 * DESPUÉS de que el usuario elija, nunca antes, porque un borrado previo convierte un
 * fallo transitorio de la lista en una pérdida definitiva de la preferencia.
 */

/**
 * Milisegundos de una fecha venga como venga.
 *
 * Hay que normalizar porque `joinedAt` cruza la API como JSON y pierde el tipo: medido
 * el 17-sep-2026, un `Timestamp` del Admin SDK se serializa como
 * `{"_seconds":1789589117,"_nanoseconds":550000000}` (no tiene `toJSON`). Pero la misma
 * función se usa en tests con `Date` y con ISO, y el SDK de cliente entrega
 * `{seconds,nanoseconds}` sin guion bajo.
 *
 * Devuelve `null` —no `0`— cuando no hay fecha utilizable: `0` es el 1-ene-1970 y
 * ordenaría a los indocumentados DELANTE de todos, que es lo contrario de lo que
 * queremos.
 */
function milisDeFecha(v) {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.getTime();
  if (typeof v === 'string') {
    const ms = Date.parse(v);
    return Number.isNaN(ms) ? null : ms;
  }
  if (typeof v.toMillis === 'function') {
    const ms = v.toMillis();
    return Number.isFinite(ms) ? ms : null;
  }
  const s = v._seconds !== undefined ? v._seconds : v.seconds;
  const n = v._nanoseconds !== undefined ? v._nanoseconds : v.nanoseconds;
  if (typeof s === 'number' && Number.isFinite(s)) {
    return s * 1000 + Math.floor((typeof n === 'number' ? n : 0) / 1e6);
  }
  return null;
}

/**
 * Orden ESTABLE de la lista, con tres niveles y ninguno opcional.
 *
 * 1. `membership.joinedAt` ascendente — la organización más antigua primero, que es el
 *    orden que una persona reconoce. Medido el 17-sep-2026: presente en 8 de 8
 *    documentos de miembro.
 * 2. `name` con `localeCompare` — para el empate real: dos pertenencias creadas en el
 *    mismo lote (una migración, un script) comparten milisegundo.
 * 3. `id` — para que la función sea TOTAL. Sin este nivel, dos organizaciones sin fecha
 *    y con el mismo nombre volverían a depender del orden de llegada, que es justo lo
 *    que esto viene a quitar.
 *
 * Los que no tienen fecha van al FINAL, no al principio (ver `milisDeFecha`).
 *
 * No muta la lista recibida: `[...orgs]`. La de `OrganizationContext` se pasa además a
 * `setOrganizations`, y ordenar in situ le cambiaría el orden al selector del panel como
 * efecto colateral invisible.
 */
function ordenarOrganizaciones(orgs) {
  return [...orgs].sort((a, b) => {
    const fa = milisDeFecha(a && a.membership && a.membership.joinedAt);
    const fb = milisDeFecha(b && b.membership && b.membership.joinedAt);
    if (fa !== fb) {
      if (fa === null) return 1;
      if (fb === null) return -1;
      return fa - fb;
    }
    const na = (a && a.name) || '';
    const nb = (b && b.name) || '';
    const porNombre = na.localeCompare(nb);
    if (porNombre !== 0) return porNombre;
    return String((a && a.id) || '').localeCompare(String((b && b.id) || ''));
  });
}

/**
 * Qué hacer al entrar.
 *
 * @param {object[]} orgs         las organizaciones del usuario, tal cual las devuelve la API
 * @param {string} [pendingOrgId] invitación recién aceptada (localStorage `selectedOrganizationId`)
 * @param {string} [savedOrgId]   elección previa, ya resuelta por quien llama: Firestore ?? localStorage
 *
 * @returns {{accion:'ninguna'}
 *          |{accion:'entrar', orgId:string, motivo:'invitacion'|'guardada'|'unica'}
 *          |{accion:'elegir', orgs:object[], guardadaInvalida:boolean}}
 *
 * PRECEDENCIA, y el orden importa:
 *   invitación > guardada > única > preguntar
 *
 * La invitación va primero porque es el acto MÁS reciente y deliberado: quien acaba de
 * aceptar una invitación quiere ver ESA organización, no la que usó la última vez.
 *
 * «Única» va DESPUÉS de «guardada» aunque el resultado sea el mismo, y no es cosmético:
 * el motivo es lo que el log distingue, y «entró donde había guardado» y «sólo tenía una»
 * son dos hechos distintos sobre el sistema.
 */
function decidirOrganizacion({ orgs = [], pendingOrgId = null, savedOrgId = null } = {}) {
  const lista = Array.isArray(orgs) ? orgs.filter((o) => o && o.id) : [];

  if (lista.length === 0) return { accion: 'ninguna' };

  const estaEnLaLista = (id) => !!id && lista.some((o) => o.id === id);

  if (estaEnLaLista(pendingOrgId)) {
    return { accion: 'entrar', orgId: pendingOrgId, motivo: 'invitacion' };
  }
  if (estaEnLaLista(savedOrgId)) {
    return { accion: 'entrar', orgId: savedOrgId, motivo: 'guardada' };
  }
  if (lista.length === 1) {
    return { accion: 'entrar', orgId: lista[0].id, motivo: 'unica' };
  }

  // `guardadaInvalida` distingue «nunca eligió» de «lo que eligió ya no vale». Sólo el
  // segundo caso merece log y limpieza: el primero es un usuario nuevo con dos
  // organizaciones, que no tiene nada roto.
  return {
    accion: 'elegir',
    orgs: ordenarOrganizaciones(lista),
    guardadaInvalida: !!savedOrgId,
  };
}

/**
 * ¿Hay que persistir la organización resuelta, o ya está guardada tal cual?
 *
 * ── LA CARRERA QUE CIERRA, MEDIDA EN PRODUCCIÓN EL 17-sep-2026 ──────────────
 * `loadOrgContext` reescribía su decisión en CADA carga, aunque no hubiera cambiado nada,
 * y esa escritura es fire-and-forget. Con dos cosas ocurriendo a la vez, la de antes
 * aterriza encima de la de después:
 *
 *   15:38:15.069  la página lista organizaciones → 1 (aún no había aceptado)
 *                 decide PUX App («única») y deja pedida la escritura
 *   15:38:18.898  el servidor acepta la invitación → escribe Brokers
 *   15:38:3x      aterriza la escritura de las 15:38:15 → PUX App PISA a Brokers
 *
 * Ocurrió DOS veces seguidas, una por invitación. El usuario aceptaba una invitación y su
 * organización activa volvía sola a la de antes, sin error y sin log.
 *
 * ── POR QUÉ ESTO BASTA ──────────────────────────────────────────────────────
 * La escritura peligrosa es precisamente la que NO aporta nada: la que reescribe lo que
 * acaba de leer. Si la decisión coincide con lo guardado no hay nada que persistir, así que
 * no se pide la escritura y no hay nada que pueda aterrizar tarde.
 *
 * Queda una ventana teórica —la decisión difiere de lo leído Y otro escritor pone un tercer
 * valor a la vez— pero ahí hay dos intenciones distintas de verdad y gana la última, que es
 * la semántica que se espera. La ventana que se cerró era otra: una escritura sin intención
 * detrás.
 *
 * ── LA SEGUNDA CONDICIÓN NO ES DECORATIVA ───────────────────────────────────
 * También se escribe si `mode` no es `'organization'`, aunque la organización coincida. La
 * escritura incondicional venía reparando ese campo de paso, y `Dashboard.js:141` EXIGE
 * `mode === 'organization'` para conmutar tras un pago: sin esta mitad, un documento con la
 * organización correcta y el `mode` mal dejaría de repararse y esa conmutación no ocurriría.
 * Medido el 17-sep-2026: hoy hay 0 documentos en ese estado, así que es una reparación que
 * no se ejerce — pero quitarla sería cambiar un comportamiento sin querer.
 *
 * @param {string|null} targetOrgId      la organización resuelta en ESTA carga
 * @param {{currentOrgId?: string, mode?: string}} savedContext  lo leído en ESTA carga
 * @returns {boolean}
 */
function debePersistirEleccion({ targetOrgId = null, savedContext = {} } = {}) {
  if (!targetOrgId) return false;
  const yaGuardada = savedContext && savedContext.currentOrgId === targetOrgId;
  const modoCorrecto = savedContext && savedContext.mode === 'organization';
  return !(yaGuardada && modoCorrecto);
}

module.exports = {
  decidirOrganizacion,
  ordenarOrganizaciones,
  milisDeFecha,
  debePersistirEleccion,
};
